import { memo } from 'react';
import type { ReactElement, ReactNode } from 'react';

import styled from 'styled-components';

import { FakeLink } from '../FakeLink';

import type {
  ListPlaceholderReload,
  ListPlaceholderRenderReload,
  ListPlaceholderRenderDescription,
} from './types';

const StyledWrapper = styled.div<{ $withoutFilters: boolean }>(
  ({ $withoutFilters, theme: { indents } }) => ({
    paddingTop: indents.INDENT_5,
    paddingBottom: indents.INDENT_5,
    display: $withoutFilters ? 'flex' : 'grid',
    gridTemplateColumns: 'auto auto',
    gridTemplateRows: 'auto auto',
    margin: '0 auto',
    maxWidth: 'fit-content',
    alignItems: $withoutFilters ? 'center' : '',
  }),
);

const StyledIconWrapper = styled.div(({ theme: { indents } }) => ({
  display: 'flex',
  justifyContent: 'center',
  paddingRight: indents.INDENT_2,
  gridRow: 'span 2',
}));

export const StyledText = styled.div(({ theme: { colors, fonts } }) => ({
  ...fonts.H6,
  color: colors.NEUTRAL_DEFAULT,
}));

export const StyledDescription = styled.div(({ theme: { colors, fonts } }) => ({
  ...fonts.LABEL02,
  color: colors.NEUTRAL_SECONDARY,
}));

export type ListPlaceholderBaseProps = {
  /**
   * Иконка
   */
  icon: ReactNode;
  /**
   * Заголовок
   */
  children: ReactNode;
  /**
   * Руководство к действиям
   * @param {Object} params - параметры
   * @param {Function} params.renderReload - функция, принимающая на вход текст, возвращает элемент, при нажатии на который будет произведён сброс фильтров
   */
  renderDescription?: ListPlaceholderRenderDescription;
  /**
   * Функция перезагрузки списка, по умолчанию очищает параметры адресной строки
   */
  reload?: ListPlaceholderReload;
};

export function ListPlaceholderBaseInner({
  icon,
  children,
  renderDescription = undefined,
  reload = () => undefined,
}: ListPlaceholderBaseProps): ReactElement {
  const renderReload: ListPlaceholderRenderReload = (text) => (
    <FakeLink component="button" type="button" onClick={reload}>
      {text}
    </FakeLink>
  );

  return (
    <StyledWrapper $withoutFilters={!renderDescription}>
      <StyledIconWrapper>{icon}</StyledIconWrapper>
      <StyledText>{children}</StyledText>

      {renderDescription && (
        <StyledDescription>
          {renderDescription({
            renderReload,
          })}
        </StyledDescription>
      )}

    </StyledWrapper>
  );
}

export const ListPlaceholderBase = memo(ListPlaceholderBaseInner);
